import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CheckCircle2,
  Clock,
  KeyRound,
  Loader2,
  PlugZap,
  RefreshCw,
  RotateCcw,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import type { Settings, SettingsInput, SourceSyncTest } from '@fenix/shared';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { settingsApi } from '@/lib/api/endpoints';
import { ApiError } from '@/lib/api/client';
import { fmtDateTime, fmtRelative } from '@/lib/format';

interface Props {
  form: SettingsInput;
  set: <K extends keyof SettingsInput>(k: K, v: SettingsInput[K]) => void;
  settings: Settings;
  isAdmin: boolean;
  /** Senha digitada e ainda não salva — fica no estado da página, junto com as outras chaves. */
  password: string;
  setPassword: (v: string) => void;
  onClearPassword: () => void;
}

const STATUS_KEY = ['settings', 'source-sync'] as const;

/** `<input type="date">` trabalha com YYYY-MM-DD; a API guarda ISO com fuso. */
const toDateInput = (iso: string | null | undefined) => (iso ? iso.slice(0, 10) : '');
const fromDateInput = (v: string) => (v ? new Date(`${v}T00:00:00`).toISOString() : null);

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 px-3 py-2">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right">{children}</span>
    </div>
  );
}

/**
 * Aba Integração: conexão com o banco de origem (buygoods_bridge) e a sincronização
 * periódica dos carrinhos abandonados. Os campos salvam pelo "Salvar" da página; o teste de
 * conexão e o "Sincronizar agora" agem na hora.
 */
export function SourceSyncTab({
  form,
  set,
  settings: s,
  isAdmin,
  password,
  setPassword,
  onClearPassword,
}: Props) {
  const qc = useQueryClient();
  const [test, setTest] = useState<SourceSyncTest | null>(null);
  const [confirmReset, setConfirmReset] = useState(false);
  const status = useQuery({
    queryKey: STATUS_KEY,
    queryFn: settingsApi.sourceSync,
    refetchInterval: 5_000,
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: STATUS_KEY });
  const onError = (fallback: string) => (e: unknown) =>
    toast.error(e instanceof ApiError ? e.message : fallback);

  const testMut = useMutation({
    // Testa o que está no formulário, salvo ou não: uma senha errada não precisa ir ao banco.
    mutationFn: () =>
      settingsApi.sourceSyncTest({
        sourceDbHost: form.sourceDbHost,
        sourceDbPort: form.sourceDbPort,
        sourceDbName: form.sourceDbName,
        sourceDbUser: form.sourceDbUser,
        sourceDbSsl: form.sourceDbSsl,
        sourceTable: form.sourceTable,
        ...(password ? { sourceDbPassword: password } : {}),
      }),
    onSuccess: (r) => {
      setTest(r);
      (r.ok ? toast.success : toast.error)(r.message);
    },
    onError: onError('Falha no teste'),
  });
  const runMut = useMutation({
    mutationFn: settingsApi.sourceSyncRun,
    onSuccess: () => {
      toast.success('Sincronização enfileirada — o worker começa em instantes');
      void invalidate();
    },
    onError: onError('Não foi possível enfileirar'),
  });
  const resetMut = useMutation({
    mutationFn: settingsApi.sourceSyncReset,
    onSuccess: () => {
      toast.success('Cursor reiniciado');
      void invalidate();
    },
    onError: onError('Não foi possível reiniciar'),
  });

  const st = status.data;
  const busy = !!st && (st.running || st.queued);
  const canRun = isAdmin && !!st?.configured && !busy;

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Banco de origem</CardTitle>
          <CardDescription>
            Onde os carrinhos abandonados chegam (buygoods_bridge). A conexão é aberta em modo
            somente-leitura; nada é escrito lá.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2 space-y-1.5">
              <Label>Host</Label>
              <Input
                placeholder="ip ou hostname do servidor"
                value={form.sourceDbHost ?? ''}
                onChange={(e) => set('sourceDbHost', e.target.value || null)}
                disabled={!isAdmin}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Porta</Label>
              <Input
                type="number"
                min={1}
                max={65535}
                value={form.sourceDbPort ?? s.sourceDbPort}
                onChange={(e) => set('sourceDbPort', Number(e.target.value) || 5432)}
                disabled={!isAdmin}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Banco</Label>
              <Input
                placeholder="buygoods_bridge"
                value={form.sourceDbName ?? ''}
                onChange={(e) => set('sourceDbName', e.target.value || null)}
                disabled={!isAdmin}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Usuário</Label>
              <Input
                placeholder="usuário somente leitura"
                value={form.sourceDbUser ?? ''}
                onChange={(e) => set('sourceDbUser', e.target.value || null)}
                disabled={!isAdmin}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label>Senha</Label>
              {s.hasSourceDbPassword ? (
                <Badge variant="secondary">configurada</Badge>
              ) : (
                <Badge variant="outline">pendente</Badge>
              )}
            </div>
            <div className="flex gap-2">
              <Input
                type="password"
                placeholder={
                  s.hasSourceDbPassword ? '•••••••• (cole para substituir)' : 'senha do usuário'
                }
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={!isAdmin}
              />
              {s.hasSourceDbPassword && isAdmin && (
                <Button
                  variant="outline"
                  size="icon"
                  title="Remover senha"
                  onClick={onClearPassword}
                >
                  <KeyRound />
                </Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Criptografada no banco, como as outras chaves.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Tabela</Label>
              <Input
                placeholder="webhook_events"
                value={form.sourceTable ?? s.sourceTable}
                onChange={(e) => set('sourceTable', e.target.value)}
                disabled={!isAdmin}
              />
              <p className="text-xs text-muted-foreground">
                Aceita <span className="font-mono">schema.tabela</span>. Trocar a tabela reinicia o
                cursor.
              </p>
            </div>
            <div className="flex items-center justify-between rounded-lg border p-3 self-start">
              <div>
                <Label>SSL</Label>
                <p className="text-xs text-muted-foreground">Exigido por alguns provedores.</p>
              </div>
              <Switch
                checked={form.sourceDbSsl ?? s.sourceDbSsl}
                onCheckedChange={(v) => set('sourceDbSsl', v)}
                disabled={!isAdmin}
              />
            </div>
          </div>

          {isAdmin && (
            <Button variant="outline" onClick={() => testMut.mutate()} disabled={testMut.isPending}>
              {testMut.isPending ? <Loader2 className="animate-spin" /> : <PlugZap />} Testar
              conexão
            </Button>
          )}

          {test && (
            <Alert variant={test.ok ? 'default' : 'destructive'}>
              {test.ok ? <CheckCircle2 className="size-4" /> : <XCircle className="size-4" />}
              <AlertTitle>{test.ok ? 'Conexão ok' : 'Falha na conexão'}</AlertTitle>
              <AlertDescription className="space-y-1">
                <p>{test.message}</p>
                {test.ok && (
                  <p className="text-xs">
                    Postgres {test.serverVersion ?? '?'} · último carrinho recebido{' '}
                    {test.newestReceivedAt ? fmtRelative(test.newestReceivedAt) : 'nunca'}
                  </p>
                )}
                {test.missingColumns.length > 0 && (
                  <p className="font-mono text-xs">{test.missingColumns.join(', ')}</p>
                )}
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Sincronização</CardTitle>
          <CardDescription>
            Leitura periódica dos carrinhos novos. Cada registro vira um lead uma única vez —
            reprocessar nunca duplica.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div>
              <Label>Importar automaticamente</Label>
              <p className="text-xs text-muted-foreground">
                Desligado, só o botão &quot;Sincronizar agora&quot; importa. Requer o worker no ar.
              </p>
            </div>
            <Switch
              checked={form.sourceSyncEnabled ?? s.sourceSyncEnabled}
              onCheckedChange={(v) => set('sourceSyncEnabled', v)}
              disabled={!isAdmin}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Intervalo (minutos)</Label>
              <Input
                type="number"
                min={1}
                max={1440}
                value={form.sourceSyncIntervalMin ?? s.sourceSyncIntervalMin}
                onChange={(e) => set('sourceSyncIntervalMin', Number(e.target.value) || 5)}
                disabled={!isAdmin}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Importar a partir de</Label>
              <Input
                type="date"
                value={toDateInput(form.sourceSyncSince)}
                onChange={(e) => set('sourceSyncSince', fromDateInput(e.target.value))}
                disabled={!isAdmin}
              />
              <p className="text-xs text-muted-foreground">
                Só vale na primeira execução (ou após reiniciar o cursor). Vazio importa todo o
                histórico.
              </p>
            </div>
          </div>

          <div className="divide-y rounded-lg border text-sm">
            <Row label="Worker">
              {st?.scheduled ? (
                <Badge variant="secondary" className="gap-1">
                  <CheckCircle2 className="size-3 text-success" /> no ar
                </Badge>
              ) : (
                <Badge variant="outline" className="gap-1">
                  <XCircle className="size-3 text-destructive" /> parado
                </Badge>
              )}
            </Row>
            <Row label="Estado">
              {st?.running ? (
                <span className="inline-flex items-center gap-1">
                  <Loader2 className="size-3 animate-spin" /> importando…
                </span>
              ) : st?.queued ? (
                <span className="inline-flex items-center gap-1">
                  <Clock className="size-3" /> na fila
                </span>
              ) : st?.enabled && st.configured ? (
                'aguardando o próximo ciclo'
              ) : (
                'parado'
              )}
            </Row>
            <Row label="Última execução">
              {st?.lastRunAt
                ? `${fmtDateTime(st.lastRunAt)} (${fmtRelative(st.lastRunAt)})`
                : 'nunca'}
            </Row>
            <Row label="Próxima execução">{st?.nextRunAt ? fmtRelative(st.nextRunAt) : '—'}</Row>
            <Row label="Leads importados">{st?.imported ?? 0}</Row>
            <Row label="Cursor (último id lido)">
              <span className="font-mono text-xs">{st?.cursor ?? '—'}</span>
            </Row>
          </div>

          {st?.lastError && (
            <Alert variant="destructive">
              <XCircle className="size-4" />
              <AlertTitle>Última execução falhou</AlertTitle>
              <AlertDescription>{st.lastError}</AlertDescription>
            </Alert>
          )}

          {isAdmin && (
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => runMut.mutate()} disabled={!canRun || runMut.isPending}>
                {runMut.isPending ? <Loader2 className="animate-spin" /> : <RefreshCw />}{' '}
                Sincronizar agora
              </Button>
              <Button
                variant="outline"
                onClick={() => setConfirmReset(true)}
                disabled={!st?.cursor || busy || resetMut.isPending}
              >
                <RotateCcw /> Reiniciar cursor
              </Button>
            </div>
          )}
          {!st?.configured && (
            <p className="text-xs text-muted-foreground">
              Preencha e salve a conexão para liberar a sincronização.
            </p>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={confirmReset} onOpenChange={setConfirmReset}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reiniciar o cursor?</AlertDialogTitle>
            <AlertDialogDescription>
              A próxima execução volta a ler a origem desde &quot;Importar a partir de&quot; (ou
              desde o início, se vazio). Leads já importados não são duplicados nem alterados de
              status — só o cursor volta.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmReset(false);
                resetMut.mutate();
              }}
            >
              Reiniciar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
